/**
 * Active-facility resolver (frontend shell doc §6, audit F11).
 *
 * The "active facility" for a facility-scoped DP screen is, in order of precedence:
 *   1. the URL query params  (Org Admin new-tab: ?facilityId=&facilityCode=&facilityName=)
 *   2. the user's own dischargeplanner facility  (plain DP / Facility Admin — legacy path)
 *
 * This is PER-TAB (read from the URL), so an Org Admin can have several facility tabs
 * open at once without them colliding (doc §2.1 / §8 — no cross-tab bleed). Org Admins
 * have NO dischargeplanner row, so for them the URL params are the only source; plain
 * DPs / Facility Admins keep working unchanged (no params -> localStorage fallback).
 *
 * getActiveFacility() returns a facility-shaped object {facilityId, facilityCode,
 * facilityName} so a read like `userInfo.dischargePlanner.facility.facilityId` becomes
 * the drop-in `getActiveFacility().facilityId`.
 */

function facilityFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const fid = p.get("facilityId");
    if (!fid) return null;
    const n = Number(fid);
    if (Number.isNaN(n)) return null;
    return {
      facilityId: n,
      facilityCode: p.get("facilityCode") || null,
      facilityName: p.get("facilityName") || null,
    };
  } catch (e) {
    return null;
  }
}

const ACTIVE_FACILITY_KEY = "_active_facility";

/**
 * Capture the facility from the URL into sessionStorage (PER-TAB) once at app start,
 * so the active facility survives in-tab navigation AND any query-string stripping the
 * DP shell does. sessionStorage is tab-isolated -> no cross-tab bleed. Call once before
 * the app renders (see index.js).
 */
export function captureActiveFacilityFromUrl() {
  try {
    const f = facilityFromUrl();
    if (f) {
      sessionStorage.setItem(ACTIVE_FACILITY_KEY, JSON.stringify(f));
    }
  } catch (e) {
    /* ignore */
  }
}

function facilityFromSession() {
  try {
    const raw = sessionStorage.getItem(ACTIVE_FACILITY_KEY);
    if (raw) {
      const f = JSON.parse(raw);
      if (f && f.facilityId != null) return f;
    }
  } catch (e) {
    /* ignore */
  }
  return null;
}

function facilityFromUserInfo() {
  try {
    const info = JSON.parse(localStorage.getItem("_user_info")) || {};
    if (
      info.dischargePlanner &&
      info.dischargePlanner.facility &&
      info.dischargePlanner.facility.facilityId
    ) {
      return info.dischargePlanner.facility;
    }
  } catch (e) {
    /* ignore */
  }
  return null;
}

/**
 * {facilityId, facilityCode, facilityName} — per-tab sessionStorage (captured from the
 * URL at load) first, then the live URL, then the user's own DP facility, else null.
 */
export function getActiveFacility() {
  return facilityFromSession() || facilityFromUrl() || facilityFromUserInfo() || null;
}

/** Just the active facility id (URL first, else own DP facility), or null. */
export function getActiveFacilityId() {
  const f = getActiveFacility();
  return f && f.facilityId != null ? f.facilityId : null;
}
